import { useId, type ComponentProps, type FC, type ReactNode } from 'react';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { cn } from '../lib/utils';
import { ChartContainer, type ChartConfig } from './chart';

type TrendChartMargin = { top?: number; right?: number; bottom?: number; left?: number };

type TrendChartProps = Omit<ComponentProps<'figure'>, 'children'> & {
  data: readonly Record<string, string | number>[];
  xKey: string;
  yKey: string;
  /** Text equivalent of the plot. Screen readers get this instead of the SVG. */
  summary: ReactNode;
  domain?: [number | 'auto', number | 'auto'];
  /** Shows the x-axis labels and vertical grid lines. */
  showXAxis?: boolean;
  /** Line colour, as a token reference such as `var(--z-brand)`. */
  color?: string;
  /** Area fill colour. Defaults to `color`. */
  fillColor?: string;
  /** Fill opacity at the top and bottom of the area gradient. */
  fillOpacity?: [number, number];
  strokeWidth?: number;
  margin?: TrendChartMargin;
  /**
   * Draws the area in when the data first arrives or changes
   * (`--z-dur-slow`, ease-out). Off under prefers-reduced-motion.
   */
  animate?: boolean;
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);

/**
 * A single-series area chart for showing a metric's recent trend, sized by
 * its container. The plot is hidden from assistive tech; `summary` carries
 * the meaning, so describe the shape of the data there.
 */
export const TrendChart: FC<TrendChartProps> = ({
  data,
  xKey,
  yKey,
  summary,
  domain = ['auto', 'auto'],
  showXAxis = false,
  color = 'var(--z-brand)',
  fillColor = color,
  fillOpacity = [0.22, 0],
  strokeWidth = 2.4,
  margin = { top: 4, right: 0, bottom: 0, left: 0 },
  animate = false,
  className,
  ...props
}) => {
  const gradientId = `trend-fill-${useId().replaceAll(':', '')}`;
  const config = { [yKey]: { color } } satisfies ChartConfig;

  return (
    <figure className={cn('h-40 w-full', className)} {...props}>
      <figcaption className="sr-only">{summary}</figcaption>
      <div aria-hidden="true" className="size-full">
        <ChartContainer config={config} className="aspect-auto size-full">
          <AreaChart data={[...data]} margin={margin} accessibilityLayer={false}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={fillColor} stopOpacity={fillOpacity[0]} />
                <stop offset="100%" stopColor={fillColor} stopOpacity={fillOpacity[1]} />
              </linearGradient>
            </defs>
            {showXAxis && <CartesianGrid horizontal={false} stroke="var(--z-border)" />}
            <XAxis
              dataKey={xKey}
              hide={!showXAxis}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 9 }}
              interval={0}
              padding={{ left: 2, right: 2 }}
            />
            <YAxis domain={domain} hide />
            <Area
              type="monotone"
              dataKey={yKey}
              stroke={`var(--color-${yKey})`}
              strokeWidth={strokeWidth}
              fill={`url(#${gradientId})`}
              dot={false}
              activeDot={false}
              isAnimationActive={animate && !prefersReducedMotion()}
              animationDuration={250}
              animationEasing="ease-out"
            />
          </AreaChart>
        </ChartContainer>
      </div>
    </figure>
  );
};
